import { FormattedMessage, useIntl } from 'react-intl';
import type CountryCode from '@alltrails/core/types/CountryCode';
import { COLOR_TEXT_ERROR, COLOR_TEXT_PLACEHOLDER, COLOR_TEXT_PRIMARY, FONT_FAMILY_PRIMARY, FONT_SIZE_200 } from '@alltrails/denali/tokens';
import { CardElement, CardElementChangeEvent } from '@recurly/react-recurly';
import { useState } from 'react';
import TextInput from '@alltrails/denali/components/TextInput';
import classNames from 'classnames';
import type PurchaseTier from '@alltrails/core/types/PurchaseTier';
import { useExperiments } from '@alltrails/experiments';
import BillingCountrySelector, { isBillingCountryEu } from '../BillingCountrySelector';
import getAvailablePlans from '../../utils/getAvailablePlans';
import { CARD_RECURLY_IDs, CARD_VALIDATION_NAMES, TokenValidationErrors } from '../../types/RecurlyTokenization';
import styles from './TokenizedCreditCardForm.module.scss';
import { getBillingNames } from './utils';

type Props = {
  billingCountry: {
    onChange: (billingCountry: CountryCode, response: Awaited<ReturnType<typeof getAvailablePlans>>) => void;
    value: string;
  };
  isComplianceCheckboxEnabled?: boolean;
  isUpgrade?: boolean;
  validationErrors: TokenValidationErrors;
  handleInputChange: (inputName: CARD_VALIDATION_NAMES, value: string | CardElementChangeEvent) => void;
  initialFullName?: string;
  purchaseTier: PurchaseTier;
};

function TokenizedCreditCardForm({
  billingCountry,
  isComplianceCheckboxEnabled = false,
  isUpgrade,
  handleInputChange,
  purchaseTier,
  initialFullName,
  validationErrors
}: Props) {
  const { nameError, postalCodeError, ccInputStateError } = validationErrors;

  const [fullName, setFullName] = useState(initialFullName || '');
  const [postalCode, setPostalCode] = useState('');
  const { firstName, lastName } = getBillingNames(fullName);

  const intl = useIntl();
  const isWebTaxesPhase1aEnabled = useExperiments()?.['web-taxes-phase-1a']?.value === 'on';
  const showBillingCountry = billingCountry && (isWebTaxesPhase1aEnabled || isBillingCountryEu(billingCountry.value as CountryCode)) && !isUpgrade;
  const selectLabel = intl.formatMessage({ defaultMessage: 'Billing country' });

  const cardElementStyles = {
    fontSize: `${FONT_SIZE_200}px`,
    fontColor: COLOR_TEXT_PRIMARY,
    fontFamily: `${FONT_FAMILY_PRIMARY}, Arial, sans-serif`,
    placeholder: {
      // placeholder styles can't be controlled via CSS since they're inside an iframe.
      color: `${ccInputStateError ? COLOR_TEXT_ERROR : COLOR_TEXT_PLACEHOLDER} !important`,
      content: {
        number: intl.formatMessage({ defaultMessage: 'Card number' }),
        cvv: 'CVV'
      }
    },
    invalid: {
      fontColor: COLOR_TEXT_ERROR
    }
  };

  return (
    <div>
      <div className={classNames(styles.formInputContainer, { [styles.complianceVariant]: isComplianceCheckboxEnabled })}>
        <div>
          <CardElement
            // Manually apply the error styles to the card element container.
            // Recurly does this automatically onChange, but we also want to apply these styles when the form is submitted with an empty input.
            className={classNames('recurly-card-element-container', { invalid: ccInputStateError })}
            style={cardElementStyles}
            onChange={e => handleInputChange(CARD_VALIDATION_NAMES.CREDIT_CARD, e)}
          />
          {ccInputStateError && <p className={styles.errorMessage}>{ccInputStateError}</p>}
        </div>
        <div className={styles.namesContainer}>
          <TextInput
            autoComplete="cc-name"
            className={classNames(styles.formInput, { [styles.invalidInput]: !!nameError })}
            onChange={(value: string) => {
              setFullName(value);
              handleInputChange(CARD_VALIDATION_NAMES.NAME, value);
            }}
            labelText={<FormattedMessage defaultMessage="Name on card" />}
            labelTextColor="tertiary"
            placeholder={intl.formatMessage({ defaultMessage: 'Name on card' })}
            name="name"
            errorText={nameError}
            value={fullName}
          />
        </div>
        {(!isWebTaxesPhase1aEnabled || billingCountry.value === 'CA' || billingCountry.value === 'US') && (
          <div>
            <TextInput
              recurlyDataId={CARD_RECURLY_IDs.POSTAL_CODE}
              className={classNames(styles.formInput, { [styles.invalidInput]: !!postalCodeError })}
              autoComplete="postal-code"
              onChange={(value: string) => {
                setPostalCode(value);
                handleInputChange(CARD_VALIDATION_NAMES.POSTAL_CODE, value);
              }}
              errorText={postalCodeError}
              labelText={<FormattedMessage defaultMessage="Postal code" />}
              labelTextColor="tertiary"
              placeholder={intl.formatMessage({ defaultMessage: 'Postal code' })}
              name="postal code"
            />
          </div>
        )}

        {showBillingCountry && (
          <BillingCountrySelector
            labelText={selectLabel}
            onChangeBillingCountry={async e => {
              const response = await getAvailablePlans(e as CountryCode, purchaseTier);
              billingCountry.onChange(e as CountryCode, response);
            }}
            billingCountryCode={billingCountry.value}
          />
        )}

        {/* Hidden inputs accessed by Recurly */}
        <input type="hidden" name="recurly-token" data-recurly="token" />
        <input type="hidden" name="first-name" data-recurly={CARD_RECURLY_IDs.FIRST_NAME} value={firstName} />
        <input type="hidden" name="last-name" data-recurly={CARD_RECURLY_IDs.LAST_NAME} value={lastName} />
        <input type="hidden" name="postal-code" data-recurly={CARD_RECURLY_IDs.POSTAL_CODE} value={postalCode} />
        <input type="hidden" name="country" data-recurly={CARD_RECURLY_IDs.COUNTRY} value={billingCountry.value} />
      </div>
    </div>
  );
}

export default TokenizedCreditCardForm;
